import type { NLPSearchResultSource } from '../../ai-answer-dialog/types';
import type { DetectionMetadata, IntentClassificationData } from '../../query-intent-api/types';

import { extractModelName } from './extract-model-name';
import { extractUniquePluginNames } from './extract-unique-plugin-names';
import { filterNonSearchMinions } from './filter-non-search-minions';
import { filterNonSearchPlugins } from './filter-non-search-plugins';
import { getRankedDocumentsWithChunks } from './get-ranked-documents-with-chunks';
import { getSearchResultsFromMinions } from './get-search-results-from-minions';
import { getSearchResultsFromPlugins } from './get-search-results-from-plugins';
import { mergeSearchExtraInfo } from './merge-search-extra-info';
import { normalizeCacheHit } from './normalize-cache-hit';
import { normalizeDetectionMetadata } from './normalize-detection-metadata';
import { normalizeElsOverview } from './normalize-els-overview';
import { type NormalizedDebuggerData, type RawDebuggerData } from './types';
import { getOrchestrationRounds } from './utils';

// --- Main Normalization Function ---
/**
 * Normalizes all debugger data from raw extra attributes.
 * This is the single entry point for preparing data for all debugger panels.
 *
 * Search results are extracted from both plugin invocations and minion outputs,
 * with plugin data taking precedence when available.
 *
 * @param extraAttributes - Raw extra attributes from the AI Answer context
 * @param sources - Sources from commonAttributes.sources
 * @param elsOptions - Person-intent ELS inputs from the AI Answer context
 * @returns Normalized data object with all data needed for each debugger panel
 */
export const normalizeDebuggerData = (
	extraAttributes: RawDebuggerData | undefined,
	sources?: NLPSearchResultSource[],
	elsOptions?: {
		intentData?: IntentClassificationData;
		detectionMetadata?: DetectionMetadata;
		intentDetectionTraceId?: string;
	},
): NormalizedDebuggerData => {
	const {
		originalQuery,
		transformedQuery,
		traceId,
		answerGenerationModel,
		cacheInfo,
		sources: extraSources = [],
		pluginInvocations = [],
		minion_outputs = [],
		rawJson,
	} = extraAttributes ?? {};

	const resolvedSources = sources !== undefined ? sources : extraSources;

	// Extract search data from both plugins and minions, then merge
	const pluginSearchInfo = getSearchResultsFromPlugins(pluginInvocations);
	const minionSearchInfo = getSearchResultsFromMinions(minion_outputs);
	const searchExtraInfo = mergeSearchExtraInfo(pluginSearchInfo, minionSearchInfo);
	const rankedDocumentsWithChunks = getRankedDocumentsWithChunks(searchExtraInfo);
	const orchestrationRounds = getOrchestrationRounds(minion_outputs);

	const elsOverview = normalizeElsOverview(elsOptions?.intentData, elsOptions?.detectionMetadata);

	// The intent-detection call and the ELS call share the same trace. Prefer the
	// dedicated intent-detection trace ID (captured from the intent-detection response
	// headers) and fall back to the ELS trace ID when it was not captured separately.
	const intentDetectionTraceId = elsOptions?.intentDetectionTraceId ?? elsOverview.elsTraceId;

	return {
		requestOverview: {
			originalQuery: originalQuery ?? null,
			transformedQuery: transformedQuery ?? null,
			traceId: traceId ?? null,
			intentDetectionTraceId: intentDetectionTraceId ?? null,
			intentDetectionTimestamp: elsOverview.timestamp,
			originalIntent: elsOverview.originalIntent,
			finalRoutingDecision: elsOverview.finalRoutingDecision,
			detectionMetadata: normalizeDetectionMetadata(elsOptions?.detectionMetadata),
			modelName: extractModelName(answerGenerationModel),
			cacheHit: normalizeCacheHit(cacheInfo),
			sources: resolvedSources,
			invokedPluginNames: extractUniquePluginNames(pluginInvocations),
		},
		rankedSearch: {
			searchExtraInfo,
			rankedDocumentsWithChunks,
		},
		rawSearch: {
			searchResults: searchExtraInfo?.searchResults ?? [],
		},
		pluginInvocations: {
			allPluginInvocations: pluginInvocations,
			nonSearchPluginInvocations: filterNonSearchPlugins(pluginInvocations),
			hasPluginInvocations: pluginInvocations ? pluginInvocations.length > 0 : false,
		},
		minions: {
			allMinionOutputs: minion_outputs,
			nonSearchMinionOutputs: filterNonSearchMinions(minion_outputs),
			hasMinionOutputs: minion_outputs ? minion_outputs.length > 0 : false,
			orchestrationRounds,
			roundCount: orchestrationRounds.length,
		},
		rawJson: {
			rawJson,
		},
		elsOverview,
	};
};
